import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { walletAPI, userAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { IndianRupee, Loader2, ArrowUpCircle, ArrowDownCircle, User, Wallet } from 'lucide-react';

const CreditDebit = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ user_id: '', type: 'credit', amount: '', remark: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTypeFilter, setUserTypeFilter] = useState('all');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { const r = await userAPI.getUsers(); setUsers(r.data?.filter(u => u.role !== 'admin') || []); } catch {}
  };

  const filteredUsers = userTypeFilter === 'all' ? users : users.filter(u => u.role === userTypeFilter);

  const handleUserSelect = (userId) => {
    setForm({ ...form, user_id: userId });
    setSelectedUser(users.find(u => u.id === userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.amount || parseFloat(form.amount) <= 0) { toast.error('Select user and enter valid amount'); return; }
    setLoading(true);
    try {
      const res = await walletAPI.creditDebit(form);
      toast.success(`${form.type === 'credit' ? 'Credit' : 'Debit'} of Rs.${form.amount} successful! New balance: Rs.${res.data.new_balance}`);
      setForm({ user_id: '', type: 'credit', amount: '', remark: '' });
      setSelectedUser(null);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Operation failed'); } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-5 animate-fade-in" data-testid="credit-debit-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Balance Credit / Debit</h1>
          <p className="text-sm text-slate-500 mt-0.5">Add or deduct balance from any user's wallet</p>
        </div>

        <Card className="border border-slate-100 shadow-sm rounded-xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">User Type Filter</Label>
                <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                  <SelectTrigger data-testid="usertype-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="super_distributor">Super Distributors</SelectItem>
                    <SelectItem value="distributor">Distributors</SelectItem>
                    <SelectItem value="retailer">Retailers</SelectItem>
                    <SelectItem value="api_user">API Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Select User *</Label>
                <Select value={form.user_id} onValueChange={handleUserSelect}>
                  <SelectTrigger data-testid="user-select"><User className="w-4 h-4 mr-2" /><SelectValue placeholder="Choose a user..." /></SelectTrigger>
                  <SelectContent>
                    {filteredUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role}) - Bal: Rs.{parseFloat(u.wallet_balance || 0).toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedUser && (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{selectedUser.name}</p>
                      <p className="text-xs text-slate-400 capitalize">{selectedUser.role} | {selectedUser.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Current Balance</p>
                      <p className="text-xl font-bold font-mono text-slate-900">&#8377;{parseFloat(selectedUser.wallet_balance || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <button type="button" onClick={() => setForm({...form, type: 'credit'})}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${form.type === 'credit' ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}
                  data-testid="credit-btn"
                >
                  <ArrowUpCircle className={`w-8 h-8 mx-auto mb-2 ${form.type === 'credit' ? 'text-green-600' : 'text-slate-400'}`} />
                  <p className={`font-bold ${form.type === 'credit' ? 'text-green-700' : 'text-slate-500'}`}>CREDIT</p>
                  <p className="text-xs text-muted-foreground">Add money</p>
                </button>
                <button type="button" onClick={() => setForm({...form, type: 'debit'})}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${form.type === 'debit' ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}
                  data-testid="debit-btn"
                >
                  <ArrowDownCircle className={`w-8 h-8 mx-auto mb-2 ${form.type === 'debit' ? 'text-red-600' : 'text-slate-400'}`} />
                  <p className={`font-bold ${form.type === 'debit' ? 'text-red-700' : 'text-slate-500'}`}>DEBIT</p>
                  <p className="text-xs text-muted-foreground">Deduct money</p>
                </button>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="number" placeholder="Enter amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} min="1" className="pl-10 text-lg font-bold font-mono h-12 rounded-xl" data-testid="amount-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Remark (optional)</Label>
                <Input placeholder="Enter remark..." value={form.remark} onChange={e => setForm({...form, remark: e.target.value})} className="h-11 rounded-xl" data-testid="remark-input" />
              </div>

              <Button type="submit" disabled={loading} className="w-full h-12 text-sm font-semibold rounded-xl text-white shadow-lg btn-press" style={{ background: form.type === 'credit' ? '#059669' : '#dc2626' }} data-testid="submit-btn">
                {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : form.type === 'credit' ? <ArrowUpCircle className="w-5 h-5 mr-2" /> : <ArrowDownCircle className="w-5 h-5 mr-2" />}
                {form.type === 'credit' ? 'Credit' : 'Debit'} Rs.{form.amount || '0'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CreditDebit;
